import { DirectionEnum } from "../../enums";
import { ICourseStationInfo } from "../../models";

const resolveCourseStationsKeepOriginalOrder = ({
  productionDirection,
  courseDirectionTemplate,
}: {
  productionDirection: DirectionEnum;
  courseDirectionTemplate?: DirectionEnum | string | null;
}): boolean => {
  const isRideA = productionDirection === DirectionEnum.PickUp;
  const isCourseA = courseDirectionTemplate !== DirectionEnum.DropOff;
  return isRideA === isCourseA;
};

export interface IAvailabilityRideStationsPreview {
  productionDirection: DirectionEnum;
  timeStationName: string;
  originStationName: string;
  destinationStationName: string;
}

const resolveStationDisplayName = ({
  stationsInfo,
  index,
}: {
  stationsInfo: ICourseStationInfo[];
  index: number;
}): string | undefined => {
  const name = stationsInfo[index]?.name?.trim();
  return name || undefined;
};

/** Ride endpoints and the station that carries availability time, after template reversal. */
export const resolveAvailabilityRideStationsPreview = ({
  stationsInfo,
  scheduleDirection,
  courseDirectionTemplate,
}: {
  stationsInfo?: ICourseStationInfo[] | null;
  scheduleDirection: DirectionEnum | string;
  courseDirectionTemplate?: DirectionEnum | string | null;
}): IAvailabilityRideStationsPreview | undefined => {
  if (!stationsInfo?.length) {
    return undefined;
  }

  const productionDirection = scheduleDirection as DirectionEnum;

  if (
    productionDirection !== DirectionEnum.PickUp &&
    productionDirection !== DirectionEnum.DropOff
  ) {
    return undefined;
  }

  const shouldKeepOriginal = resolveCourseStationsKeepOriginalOrder({
    productionDirection,
    courseDirectionTemplate,
  });

  const lastIndex = stationsInfo.length - 1;
  const originIndex = shouldKeepOriginal ? 0 : lastIndex;
  const destinationIndex = shouldKeepOriginal ? lastIndex : 0;
  const timeStationIndex =
    productionDirection === DirectionEnum.PickUp
      ? destinationIndex
      : originIndex;

  const originStationName = resolveStationDisplayName({
    stationsInfo,
    index: originIndex,
  });
  const destinationStationName = resolveStationDisplayName({
    stationsInfo,
    index: destinationIndex,
  });
  const timeStationName = resolveStationDisplayName({
    stationsInfo,
    index: timeStationIndex,
  });

  if (!originStationName || !destinationStationName || !timeStationName) {
    return undefined;
  }

  return {
    productionDirection,
    timeStationName,
    originStationName,
    destinationStationName,
  };
};

export interface ICourseRideEndpointsPreview {
  direction: DirectionEnum;
  originStationName: string;
  destinationStationName: string;
}

const resolveRideEndpointsForProductionDirection = ({
  stationsInfo,
  productionDirection,
  courseDirectionTemplate,
}: {
  stationsInfo: ICourseStationInfo[];
  productionDirection: DirectionEnum;
  courseDirectionTemplate?: DirectionEnum | string | null;
}): ICourseRideEndpointsPreview | undefined => {
  const shouldKeepOriginal = resolveCourseStationsKeepOriginalOrder({
    productionDirection,
    courseDirectionTemplate,
  });

  const lastIndex = stationsInfo.length - 1;
  const originIndex = shouldKeepOriginal ? 0 : lastIndex;
  const destinationIndex = shouldKeepOriginal ? lastIndex : 0;
  const originStationName = resolveStationDisplayName({
    stationsInfo,
    index: originIndex,
  });
  const destinationStationName = resolveStationDisplayName({
    stationsInfo,
    index: destinationIndex,
  });

  if (!originStationName || !destinationStationName) {
    return undefined;
  }

  return {
    direction: productionDirection,
    originStationName,
    destinationStationName,
  };
};

/** Both ride directions with from/to stations; B-template lists B before A. */
export const resolveCourseTemplateRideEndpointsList = ({
  stationsInfo,
  courseDirectionTemplate,
}: {
  stationsInfo?: ICourseStationInfo[] | null;
  courseDirectionTemplate?: DirectionEnum | string | null;
}): ICourseRideEndpointsPreview[] | undefined => {
  if (!stationsInfo?.length) {
    return undefined;
  }

  const rideA = resolveRideEndpointsForProductionDirection({
    stationsInfo,
    productionDirection: DirectionEnum.PickUp,
    courseDirectionTemplate,
  });
  const rideB = resolveRideEndpointsForProductionDirection({
    stationsInfo,
    productionDirection: DirectionEnum.DropOff,
    courseDirectionTemplate,
  });

  if (!rideA || !rideB) {
    return undefined;
  }

  return courseDirectionTemplate === DirectionEnum.DropOff
    ? [rideB, rideA]
    : [rideA, rideB];
};
